import React, { useState, useEffect } from "react";
import api from "../config/api";
import "./EditModal.css";
import Select from "react-select";
import { toast } from "react-toastify";
import { EMPTY_VALUE } from "../utils/display";
import { formSelectStyles } from "../utils/selectStyles";

interface EditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  type: "coder" | "supervisor";
  data: any;
}

const EditModal: React.FC<EditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  type,
  data,
}) => {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    enterpriseId: null as number | null,
    is_active: true,
  });
  const [enterprises, setEnterprises] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enterprisesLoaded, setEnterprisesLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && data) {
      setFormData({
        first_name: data.first_name || "",
        last_name: data.last_name || "",
        email: data.email || "",
        // A coder only carries the nested `enterprise` relation — its entity has no
        // `enterpriseId` property, unlike a supervisor. Read the relation first,
        // otherwise the select opens empty and saving clears the assignment.
        enterpriseId: data.enterprise?.id ?? data.enterpriseId ?? null,
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    }
  }, [isOpen, data]);

  useEffect(() => {
    if (isOpen) {
      fetchEnterprises();
    }
  }, [isOpen]);

  const fetchEnterprises = async () => {
    try {
      // The endpoint answers { data, pagination } and paginates at 50 by default;
      // this dropdown needs every enterprise, so ask for a single large page.
      // Older deployments still answer with a bare array, hence the shape check.
      const response = await api.get("/api/enterprises?limit=1000");
      const list = Array.isArray(response.data)
        ? response.data
        : response.data?.data ?? [];
      setEnterprises(list);
      setEnterprisesLoaded(true);
    } catch (error) {
      console.error("Error fetching enterprises:", error);
      setEnterprisesLoaded(true); // Still set to true to allow form submission
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Only the enterprise is owned locally. The other fields come from AIA and
      // are rewritten by every sync, so sending them back would be a no-op at
      // best — the coder route ignores them, the supervisor route would persist
      // them until the next sync reverted them.
      const response = await api.put(`/api/${type}s/${data.id}`, {
        enterpriseId: formData.enterpriseId,
      });
      onSave(response.data);
      onClose();
    } catch (error: any) {
      console.error(`Error updating ${type}:`, error);
      // Better error handling
      let errorMessage = `Failed to update ${type}. Please try again.`;

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 500) {
        errorMessage = `Server error. Please check that the selected enterprise exists.`;
      } else if (error.response?.status === 404) {
        errorMessage = `${type} not found. Please refresh the page.`;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content modal-content-edit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Modifier {type === "coder" ? "Codeur" : "Superviseur"}</h2>
          <button className="modal-close" onClick={onClose}>
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>Prénom</label>
            <div className="form-readonly">{formData.first_name || EMPTY_VALUE}</div>
          </div>

          <div className="form-group">
            <label>Nom</label>
            <div className="form-readonly">{formData.last_name || EMPTY_VALUE}</div>
          </div>

          <div className="form-group">
            <label>Email</label>
            <div className="form-readonly">{formData.email || EMPTY_VALUE}</div>
          </div>

          <div className="form-group">
            <label htmlFor="enterpriseId">Entreprise</label>
            <Select
              value={
                formData.enterpriseId
                  ? enterprises.find((e) => e.id === formData.enterpriseId)
                    ? {
                        value: formData.enterpriseId,
                        label: `${
                          enterprises.find(
                            (e) => e.id === formData.enterpriseId
                          )?.nom
                        } (${
                          enterprises.find(
                            (e) => e.id === formData.enterpriseId
                          )?.country
                        })`,
                      }
                    : null
                  : null
              }
              onChange={(option) =>
                setFormData((prev) => ({
                  ...prev,
                  enterpriseId: option ? option.value : null,
                }))
              }
              options={[
                { value: null, label: "Aucune Entreprise (Optionnel)" },
                ...enterprises.map((enterprise) => ({
                  value: enterprise.id,
                  label: `${enterprise.nom} (${enterprise.country})`,
                })),
              ]}
              styles={formSelectStyles}
              placeholder="Aucune Entreprise (Optionnel)"
              isClearable
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
            {enterprisesLoaded && enterprises.length === 0 && (
              <div className="form-warning">
                ⚠️ Aucune entreprise disponible. Vous pouvez toujours
                sauvegarder sans sélectionner d'entreprise.
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Actif</label>
            <div className="form-readonly">
              {formData.is_active ? "Oui" : "Non"}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Annuler
            </button>
            <button type="submit" className="btn-primary" disabled={loading}>
              {loading ? "Sauvegarde..." : "Sauvegarder"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditModal;
